import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, RefreshCw, Link2, AlertTriangle, CheckCircle2, XCircle, Upload, CreditCard, Users } from 'lucide-react';

const STATUS_BADGE = { CONNECTED: 'bg-green-500 text-white', DISCONNECTED: 'bg-slate-400 text-white', ERROR: 'bg-red-500 text-white' };
const SYNC_BADGE = { SYNCED: 'bg-green-500 text-white', PENDING: 'bg-amber-500 text-white', FAILED: 'bg-red-500 text-white' };
const PROVIDERS = ['XERO', 'MYOB', 'QUICKBOOKS'];

export default function AccountingIntegrationPage() {
  const [connections, setConnections] = useState([]);
  const [contacts, setContacts] = useState({ items: [], total: 0 });
  const [invoices, setInvoices] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(false);
  const [newProvider, setNewProvider] = useState('XERO');
  const [tab, setTab] = useState('connections');
  const [syncing, setSyncing] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [connRes, contRes, invRes] = await Promise.all([
        api.get('/integrations/accounting/connections'),
        api.get('/integrations/accounting/contacts?pageSize=50'),
        api.get('/integrations/accounting/invoices?pageSize=50'),
      ]);
      setConnections(connRes.data.items || []);
      setContacts({ items: contRes.data.items || [], total: contRes.data.total });
      setInvoices({ items: invRes.data.items || [], total: invRes.data.total });
    } catch { toast.error('Failed to load accounting data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConnect = async () => {
    try {
      await api.post('/integrations/accounting/connections', { provider: newProvider, config: {} });
      toast.success(`${newProvider} connected`); setShowConnect(false); load();
    } catch (e) { toast.error(e.response?.data?.detail?.message || 'Failed to connect'); }
  };

  const handleSyncContact = async (participantId) => {
    setSyncing(`contact-${participantId}`);
    try {
      const { data } = await api.post(`/integrations/accounting/sync-contact/${participantId}`);
      toast.success(data.data.success ? 'Contact synced' : 'Sync failed'); load();
    } catch (e) { toast.error(e.response?.data?.detail?.message || 'Sync failed'); }
    finally { setSyncing(''); }
  };

  const handleExportInvoice = async (invoiceId) => {
    setSyncing(`invoice-${invoiceId}`);
    try {
      const { data } = await api.post(`/integrations/accounting/export-invoice/${invoiceId}`);
      toast.success(data.data.success ? 'Invoice exported' : 'Export failed'); load();
    } catch (e) { toast.error(e.response?.data?.detail?.message || 'Export failed'); }
    finally { setSyncing(''); }
  };

  const handleSyncPayments = async () => {
    setSyncing('payments');
    try {
      const { data } = await api.post('/integrations/accounting/sync-payments');
      toast.success(`Payments synced: ${data.data.updated} updated`); load();
    } catch (e) { toast.error(e.response?.data?.detail?.message || 'Sync failed'); }
    finally { setSyncing(''); }
  };

  const activeConnection = connections.find(c => c.status === 'CONNECTED');

  return (
    <div className="space-y-6" data-testid="accounting-integration-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Accounting Integration</h1>
        <div className="flex gap-2">
          {activeConnection && <Button variant="outline" onClick={handleSyncPayments} disabled={syncing === 'payments'} data-testid="sync-payments-btn"><CreditCard className="w-4 h-4 mr-2" />{syncing === 'payments' ? 'Syncing...' : 'Sync Payments'}</Button>}
          <Button onClick={() => setShowConnect(true)} data-testid="connect-btn"><Plus className="w-4 h-4 mr-2" />Connect</Button>
        </div>
      </div>

      <div className="flex gap-2">
        {['connections', 'contacts', 'invoices'].map(t => (
          <Button key={t} variant={tab === t ? 'default' : 'outline'} size="sm" onClick={() => setTab(t)} data-testid={`tab-${t}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</Button>
        ))}
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : tab === 'connections' ? (
        <div className="grid gap-3">
          {connections.map(c => (
            <Card key={c.id}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <Link2 className="w-5 h-5" />
                  <div>
                    <p className="font-medium">{c.provider}</p>
                    <div className="flex gap-2 mt-1">
                      <Badge className={STATUS_BADGE[c.status]}>{c.status}</Badge>
                      {c.lastSyncAt && <span className="text-xs text-muted-foreground">Last sync: {new Date(c.lastSyncAt).toLocaleString()}</span>}
                    </div>
                    {c.lastError && <p className="text-xs text-red-500 mt-1">{c.lastError}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {connections.length === 0 && <p className="text-center text-muted-foreground py-8">No connections configured</p>}
        </div>
      ) : tab === 'contacts' ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{contacts.total} contacts synced</p>
          {contacts.items.map(c => (
            <Card key={c.id}>
              <CardContent className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{c.participantName || c.participantId}</p>
                    <div className="flex gap-2 mt-1">
                      <Badge className={SYNC_BADGE[c.syncStatus]}>{c.syncStatus}</Badge>
                      <span className="text-xs text-muted-foreground">{c.provider}</span>
                    </div>
                    {c.lastError && <p className="text-xs text-red-500">{c.lastError}</p>}
                  </div>
                </div>
                <Button size="sm" variant="outline" disabled={syncing === `contact-${c.participantId}`} onClick={() => handleSyncContact(c.participantId)} data-testid={`sync-contact-${c.id}`}><RefreshCw className="w-3 h-3 mr-1" />Re-sync</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{invoices.total} invoices linked</p>
          {invoices.items.map(inv => (
            <Card key={inv.id}>
              <CardContent className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium">{inv.invoiceNumber || inv.invoiceId}</p>
                  <div className="flex gap-2 mt-1">
                    <Badge className={SYNC_BADGE[inv.syncStatus]}>{inv.syncStatus}</Badge>
                    <Badge variant="outline">{inv.paymentStatus}</Badge>
                    {inv.totalAmount && <span className="text-xs text-muted-foreground">${inv.totalAmount?.toFixed(2)}</span>}
                  </div>
                  {inv.lastError && <p className="text-xs text-red-500">{inv.lastError}</p>}
                </div>
                <Button size="sm" variant="outline" disabled={syncing === `invoice-${inv.invoiceId}`} onClick={() => handleExportInvoice(inv.invoiceId)} data-testid={`export-invoice-${inv.id}`}><Upload className="w-3 h-3 mr-1" />Re-export</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showConnect} onOpenChange={setShowConnect}>
        <DialogContent>
          <DialogHeader><DialogTitle>Connect Accounting Provider</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Provider</Label>
              <Select value={newProvider} onValueChange={setNewProvider}>
                <SelectTrigger data-testid="provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PROVIDERS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">v1 uses a mock connector. Configure real OAuth when ready.</p>
          </div>
          <DialogFooter><Button onClick={handleConnect} data-testid="confirm-connect-btn">Connect</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
